import { useCallback, useEffect, useMemo, useState } from 'react';
// Types
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
// Utils
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import _, { isEmpty } from 'lodash';
import {
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
// Slices
import {
  organizationMapSelector,
  organizationNodesSelector,
} from 'slices/organizationHierarchy.slice';
import {
  reservationModesMapSelector,
  reservationModesSelector,
} from 'slices/reservationMode.slice';
import {
  copyReservationTemplate,
  createUnsavedReservationTemplate,
  deleteReservationTemplateItem,
  getFirstDrawer,
  getListIdSelected,
  reservationTemplatesLoading,
  reservationTemplatesSelector,
  selectedReservationTemplateMap,
  setSelectedReservationTemplate,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  ETemplateType,
  type TOrganizationNode,
  type TReservationMode,
  type TReservationTemplate,
  type TType,
} from '@timeedit/types';
// Components
import {
  Button,
  type Column,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Dropdown, type MenuProps, Space } from '@timeedit/ui-components/antd';
// Styles
import './ReservationTemplateList.scss';

interface IRowData {
  id: number;
  name: string;
  templateType: string;
  types: string;
  reservationModes: string;
  organizations: string;
  active: string;
}

interface Props {
  confirmDiscardTemplateChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const ReservationTemplateList = ({ confirmDiscardTemplateChanges }: Props) => {
  const dispatch = useAppDispatch();

  const reservationTemplateMap: Record<number, TReservationTemplate> =
    useAppSelector(selectedReservationTemplateMap);
  const reservationTemplates: TReservationTemplate[] = useAppSelector(
    reservationTemplatesSelector
  );
  const highlightIdsSelect = useAppSelector(getListIdSelected);
  const objectTypes: TType[] = useAppSelector(selectObjectTypesList);
  const objectTypeMap = useAppSelector(selectObjectTypesMap);
  const reservationModes: TReservationMode[] = useAppSelector(
    reservationModesSelector
  );
  const reservationModeMap = useAppSelector(reservationModesMapSelector);
  const organizationNodes: TOrganizationNode[] = useAppSelector(
    organizationNodesSelector
  );
  const organizationNodeById = useAppSelector(organizationMapSelector);
  const loading = useAppSelector(reservationTemplatesLoading);

  const [highlightIds, setHighlightIds] = useState<number[]>([]);
  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(highlightIdsSelect);
    }, 100);
  }, [highlightIdsSelect.join('')]);

  const onCreateNew = useCallback((templateType: ETemplateType) => {
    dispatch(createUnsavedReservationTemplate(templateType));
  }, []);

  const onDuplicate = useCallback(
    (item: IRowData) => {
      const templateId = item.id;
      const template = reservationTemplateMap[templateId];
      dispatch(copyReservationTemplate(template));
    },
    [reservationTemplateMap]
  );

  const onRowClick = useCallback(
    (item: IRowData) =>
      confirmDiscardTemplateChanges(true, () =>
        dispatch(
          setSelectedReservationTemplate(true, reservationTemplateMap[item.id])
        )
      ),
    [dispatch, reservationTemplateMap]
  );

  const onRow = useRowProps<IRowData>(onRowClick, highlightIds);

  const onCompare = useCallback(
    (item: IRowData) => {
      const templateId = item.id;
      const currentReservationTemplate = reservationTemplateMap[templateId];
      if (!currentReservationTemplate) return;

      const firstDrawer = dispatch(getFirstDrawer());
      if (isEmpty(firstDrawer)) {
        dispatch(
          setSelectedReservationTemplate(true, currentReservationTemplate)
        );
      } else {
        confirmDiscardTemplateChanges(false, () =>
          dispatch(
            setSelectedReservationTemplate(false, currentReservationTemplate)
          )
        );
      }
    },
    [dispatch, reservationTemplateMap]
  );

  const objectTypeOptions = useMemo(
    () =>
      objectTypes
        .map((type) => ({
          value: type.id.toString(),
          label: type.name,
        }))
        .sort((t1, t2) => t1.label.localeCompare(t2.label)),
    [objectTypes]
  );

  const modeOptions = useMemo(
    () =>
      reservationModes
        .map((mode) => ({
          value: mode.id.toString(),
          label: mode.name,
        }))
        .sort((t1, t2) => t1.label.localeCompare(t2.label)),
    [reservationModes]
  );

  const organizationOptions: any = useMemo(
    () =>
      organizationNodes
        .map((org) => ({
          value: org.id,
          label: org.name,
        }))
        .sort((t1, t2) => t1.label.localeCompare(t2.label)),
    [organizationNodes]
  );

  const templateTypeOptions = useMemo(
    () =>
      Object.values(ETemplateType)
        .map((type) => ({
          value: type,
          label: getText(type),
        }))
        .sort((t1, t2) => t1.label.localeCompare(t2.label)),
    []
  );

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const shouldFilterKey = useCallback(
    (key: string, filters: Record<string, any>) =>
      key in filters && filters[key] !== undefined,
    []
  );

  const filteredTemplates = useMemo(
    () =>
      reservationTemplates.filter((template) => {
        const filters = filterSettings.filters;
        const freeTextSearch = filterSettings.freeTextSearch;
        let matchedType = true;
        let matchedReservationMode = true;
        let matchedOrganization = true;
        let matchedActive = true;
        let matchedText = true;
        let matchedTemplateType = true;
        if (shouldFilterKey('templateType', filters)) {
          matchedTemplateType = template.templateType === filters.templateType;
        }
        if (shouldFilterKey('objectType', filters)) {
          matchedType = (template.types || [])
            .map((type) => type.type)
            .includes(Number(filters.objectType));
        }
        if (shouldFilterKey('reservationMode', filters)) {
          matchedReservationMode = (template.reservationModes || []).includes(
            Number(filters.reservationMode)
          );
        }
        if (shouldFilterKey('organization', filters)) {
          matchedOrganization = (template.organizations || [])
            .map((org) => String(org))
            .includes(filters.organization as string);
        }
        if (shouldFilterKey('active', filters)) {
          matchedActive = template.active === filters.active;
        }
        if (freeTextSearch) {
          matchedText = (template.name || '')
            .toLowerCase()
            .includes(freeTextSearch.toLowerCase());
        }
        return (
          matchedType &&
          matchedTemplateType &&
          matchedReservationMode &&
          matchedOrganization &&
          matchedActive &&
          matchedText
        );
      }),
    [reservationTemplates, filterSettings]
  );

  const dataSources = useMemo(
    () =>
      filteredTemplates.map((template) => {
        const types = (template.types || []).reduce((acc, type) => {
          const typeName = _.get(objectTypeMap, [
            type.type!.toString(),
            'name',
          ]);
          if (typeName) return acc + (acc ? ', ' : '') + typeName;
          return acc;
        }, '');
        const modes = (template.reservationModes || []).reduce((acc, id) => {
          const mode = _.get(reservationModeMap, [id.toString(), 'name']);
          if (mode) return acc + (acc ? ', ' : '') + mode;
          return acc;
        }, '');
        const organizations = (template.organizations || []).reduce(
          (acc, id) => {
            const organization = _.get(organizationNodeById, [
              id.toString(),
              'name',
            ]);
            if (organization) return acc + (acc ? ', ' : '') + organization;
            return acc;
          },
          ''
        );
        return {
          id: template.id,
          name: template.name,
          templateType: getText(template.templateType),
          types,
          reservationModes: modes,
          organizations,
          active: template.active ? 'Yes' : 'No',
        } as IRowData;
      }),
    [filteredTemplates, organizationNodeById, reservationModeMap, objectTypeMap]
  );

  const onDelete = useCallback(
    (record: IRowData) => {
      confirmDelete(record.name, () => {
        dispatch(deleteReservationTemplateItem(record.id));
      });
    },
    [confirmDelete, dispatch]
  );

  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(getFirstDrawer())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  const createNewMenu: MenuProps = useMemo(
    () => ({
      items: [
        {
          key: ETemplateType.AVAILABILITY,
          label: getText(ETemplateType.AVAILABILITY),
        },
        {
          key: ETemplateType.INFO_RESERVATION,
          label: getText(ETemplateType.INFO_RESERVATION),
        },
        {
          key: ETemplateType.RESERVATION,
          label: getText(ETemplateType.RESERVATION),
        },
      ],
      onClick: (info) => {
        onCreateNew(info.key as ETemplateType);
      },
    }),
    [onCreateNew]
  );

  const Footer = useCallback(
    () => (
      <Space>
        <Dropdown menu={createNewMenu} trigger={['click']}>
          <Button size="small" type="primary" onClick={() => {}}>
            Create new
          </Button>
        </Dropdown>
      </Space>
    ),
    [createNewMenu]
  );

  const filterOptions = useMemo(
    () => ({
      objectType: objectTypeOptions,
      reservationMode: modeOptions,
      organization: organizationOptions,
      templateType: templateTypeOptions,
    }),
    [objectTypeOptions, modeOptions, organizationOptions, templateTypeOptions]
  );

  const title = useTableTitle<IRowData>({
    total: reservationTemplates.length,
    filteredAmount: dataSources.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps<IRowData>();

  return (
    <div className="reservation-template-list" ref={contentRef}>
      <Filter
        settings={filterSettings}
        onChange={setFilterSettings}
        defaultFields={defaultFields}
        options={filterOptions}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={dataSources}
          columns={columns}
          onRow={onRow}
          getMenuProps={menuProps}
          footer={Footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default ReservationTemplateList;

const defaultFields: TEIFilterField[] = [
  {
    key: 'templateType',
    label: 'Template type',
    type: TEEFilterType.text,
  },
  {
    key: 'objectType',
    label: 'Object type',
    type: TEEFilterType.text,
  },
  {
    key: 'reservationMode',
    label: 'Reservation mode',
    type: TEEFilterType.text,
  },
  {
    key: 'organization',
    label: 'Organization',
    type: TEEFilterType.text,
  },
  {
    key: 'active',
    label: 'Active',
    type: TEEFilterType.boolean,
  },
];

const columns: Column<IRowData>[] = [
  {
    title: 'Template name',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
    render: (name: string) => <span>{name || 'New Reservation Template'}</span>,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.name.localeCompare(template2.name),
    fixed: 'left',
  },
  {
    title: 'Template type',
    dataIndex: 'templateType',
    key: 'templateType',
    ellipsis: true,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.templateType.localeCompare(template2.templateType),
  },
  {
    title: 'Object type',
    dataIndex: 'types',
    key: 'types',
    ellipsis: true,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.types.localeCompare(template2.types),
  },
  {
    title: 'Reservation mode',
    dataIndex: 'reservationModes',
    key: 'reservationModes',
    ellipsis: true,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.reservationModes.localeCompare(template2.reservationModes),
  },
  {
    title: 'Organization',
    dataIndex: 'organizations',
    key: 'organizations',
    ellipsis: true,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.organizations.localeCompare(template2.organizations),
  },
  {
    title: 'Active',
    dataIndex: 'active',
    key: 'active',
    width: 80,
    sorter: (template1: IRowData, template2: IRowData) =>
      template1.active.localeCompare(template2.active),
    fixed: 'right',
  },
];
